import {
  createDuplicateScanGuard,
  type DuplicateScanGuard,
} from './duplicateScanGuard';

export type ScanCallback = (ticketCode: string) => void;

export interface ScannerLease {
  release(): void;
}

export interface KeyboardScannerOptions {
  duplicatePreventionWindowMs?: number;
  incompleteScanTimeoutMs?: number;
  maxBufferLength?: number;
  documentRef?: Document;
  windowRef?: Window;
  now?: () => number;
}

const DEFAULT_INCOMPLETE_SCAN_TIMEOUT_MS = 1000;
const DEFAULT_MAX_BUFFER_LENGTH = 512;

export class KeyboardScanner {
  private callback: ScanCallback | null = null;
  private buffer = '';
  private timeout: number | null = null;
  private isListening = false;
  private leaseOwner: string | null = null;
  private leaseToken: symbol | null = null;

  private readonly duplicates: DuplicateScanGuard;
  private readonly incompleteScanTimeoutMs: number;
  private readonly maxBufferLength: number;
  private readonly configuredDocumentRef?: Document;
  private readonly configuredWindowRef?: Window;
  private documentRef: Document | null = null;
  private windowRef: Window | null = null;

  constructor(options: KeyboardScannerOptions = {}) {
    this.duplicates = createDuplicateScanGuard(
      options.duplicatePreventionWindowMs,
      options.now
    );
    this.incompleteScanTimeoutMs =
      options.incompleteScanTimeoutMs ?? DEFAULT_INCOMPLETE_SCAN_TIMEOUT_MS;
    this.maxBufferLength = options.maxBufferLength ?? DEFAULT_MAX_BUFFER_LENGTH;
    this.configuredDocumentRef = options.documentRef;
    this.configuredWindowRef = options.windowRef;
  }

  start(callback: ScanCallback): void {
    if (this.isListening) return;
    const documentRef = this.resolveDocumentRef();
    const windowRef = this.resolveWindowRef();
    this.callback = callback;
    this.isListening = true;
    this.documentRef = documentRef;
    this.windowRef = windowRef;
    this.leaseOwner = null;
    this.leaseToken = null;
    this.buffer = '';
    documentRef.addEventListener('keydown', this.handleKeyDown);
    documentRef.addEventListener('paste', this.handlePaste);
  }

  stop(): void {
    if (!this.isListening) return;
    const documentRef = this.documentRef;
    this.isListening = false;
    this.leaseOwner = null;
    this.leaseToken = null;
    this.callback = null;
    this.clearBuffer();
    documentRef?.removeEventListener('keydown', this.handleKeyDown);
    documentRef?.removeEventListener('paste', this.handlePaste);
    this.documentRef = null;
    this.windowRef = null;
  }

  acquire(owner: string): ScannerLease {
    if (!owner) throw new Error('Scanner lease owner is required');
    if (!this.isListening) {
      throw new Error('Scanner must be started before acquire');
    }
    if (this.leaseOwner !== null) {
      throw new Error(`Scanner already acquired by ${this.leaseOwner}`);
    }

    const leaseToken = Symbol(owner);
    let released = false;
    this.leaseOwner = owner;
    this.leaseToken = leaseToken;
    this.clearBuffer();

    return {
      release: () => {
        if (released) return;
        released = true;
        if (this.leaseToken !== leaseToken) return;
        this.leaseOwner = null;
        this.leaseToken = null;
        this.clearBuffer();
      },
    };
  }

  private isInputTarget(target: EventTarget | null): boolean {
    const windowRef = this.windowRef;
    if (!(windowRef && target)) return false;
    const browserWindow = windowRef as Window & typeof globalThis;

    return (
      target instanceof browserWindow.HTMLInputElement ||
      target instanceof browserWindow.HTMLTextAreaElement ||
      (target instanceof browserWindow.HTMLElement && target.isContentEditable)
    );
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    const windowRef = this.windowRef;
    if (
      !(this.isListening && this.callback && windowRef) ||
      this.leaseOwner !== null
    ) {
      return;
    }

    if (this.isInputTarget(event.target)) return;
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      this.flushBuffer();
      return;
    }

    if (event.key.length !== 1) return;

    this.buffer += event.key;
    if (this.buffer.length > this.maxBufferLength) {
      this.clearBuffer();
      return;
    }

    if (this.timeout !== null) windowRef.clearTimeout(this.timeout);
    this.timeout = windowRef.setTimeout(() => {
      this.flushBuffer();
    }, this.incompleteScanTimeoutMs);
  };

  private flushBuffer(): void {
    if (this.buffer.length === 0) return;

    const scannedCode = this.buffer;
    this.clearBuffer();
    this.acceptScannedCode(scannedCode);
  }

  private handlePaste = (event: ClipboardEvent): void => {
    if (!(this.isListening && this.callback) || this.leaseOwner !== null) {
      return;
    }

    if (this.isInputTarget(event.target)) return;

    const pastedCode = event.clipboardData?.getData('text').trim();
    if (!pastedCode) return;

    event.preventDefault();
    this.clearBuffer();
    this.acceptScannedCode(pastedCode);
  };

  private acceptScannedCode(scannedCode: string): void {
    if (this.duplicates.check(scannedCode) !== null) return;
    this.callback?.(scannedCode);
  }

  private clearBuffer(): void {
    this.buffer = '';
    if (this.timeout !== null) {
      this.windowRef?.clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  private resolveDocumentRef(): Document {
    if (this.configuredDocumentRef) return this.configuredDocumentRef;
    if (typeof document !== 'undefined') return document;
    throw new Error('Scanner requires a browser document');
  }

  private resolveWindowRef(): Window {
    if (this.configuredWindowRef) return this.configuredWindowRef;
    if (typeof window !== 'undefined') return window;
    throw new Error('Scanner requires a browser window');
  }
}
